<template>
  <Message v-if="!loading && !movimentiIgnoti.length" severity="info" :closable="false">
    Nessun movimento ignoto da visualizzare.
  </Message>
  <div v-for="movimentoIgnoto in movimentiIgnoti" :key="movimentoIgnoto.id" class="movimento-ignoto-row mb-4" :class="movimentoIgnoto.suggerito ? 'is-suggested' : 'is-pending'">
    <div class="movement-layout">
      <div class="movement-panel">
        <div class="movement-heading">
          <div class="movement-title-group">
            <div class="panel-title">Movimento #{{ movimentoIgnoto.estratto_conto_id }}</div>
            <Button
              v-if="canSuggest && !isAssociated(movimentoIgnoto)"
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger movement-delete-button"
              :loading="deleting === movimentoIgnoto.id"
              :disabled="deleting !== null"
              aria-label="Rimuovi classificazione come movimento ignoto"
              v-tooltip.top="'Rimuovi classificazione come movimento ignoto'"
              @click="$emit('request-delete', movimentoIgnoto)"
            />
          </div>
          <span v-if="isAssociated(movimentoIgnoto)" class="status-badge status-associated">
            <i class="pi pi-check-circle"></i> Già associato
          </span>
          <span v-else-if="movimentoIgnoto.suggerito" class="status-badge status-suggested">
            <i class="pi pi-eye"></i> Già suggerito
          </span>
          <span v-else class="status-badge status-pending">
            <i class="pi pi-clock"></i> Da suggerire
          </span>
        </div>
        <div class="movement-amount">{{ Number(movimentoIgnoto.importo).toFixed(2) }} EUR</div>
        <div class="movement-description">{{ movimentoIgnoto.descrizione }}</div>
        <div class="movement-details">
          <div><span>Data contabile</span><strong>{{ formatDate(movimentoIgnoto.data_contabile) }}</strong></div>
          <div><span>Data valuta</span><strong>{{ formatDate(movimentoIgnoto.data_valuta) }}</strong></div>
          <div><span>Tipologia</span><strong>{{ movimentoIgnoto.type_pay_name || '-' }}</strong></div>
          <div><span>Banca</span><strong>{{ movimentoIgnoto.type_pay_mode_name || '-' }}</strong></div>
          <div class="movement-history-list">
            <div class="movement-history"><span>Creato da</span><strong>{{ createdBy(movimentoIgnoto) }}</strong><span>il</span><strong>{{ formatDateTime(movimentoIgnoto.data_creazione) }}</strong></div>
            <div v-if="movimentoIgnoto.suggerito" class="movement-history"><span>Suggerito da</span><strong>{{ suggestedBy(movimentoIgnoto) }}</strong><span>il</span><strong>{{ formatDateTime(movimentoIgnoto.data_suggerimento) }}</strong></div>
            <div v-if="isAssociated(movimentoIgnoto)" class="movement-history"><span>Associato da</span><strong>{{ managedBy(movimentoIgnoto) }}</strong><span>il</span><strong>{{ formatDateTime(movimentoIgnoto.data_gestione || movimentoIgnoto.estratto_conto_data_gestione) }}</strong></div>
          </div>
        </div>
      </div>
      <div class="suggestions-panel">
        <div class="panel-title">Possibili associazioni ({{ movimentoIgnoto.suggestions.length }})</div>
        <div v-if="canSuggest && !isAssociated(movimentoIgnoto) && !movimentoIgnoto.suggerito && movimentoIgnoto.suggestions.length && !manualSuggestionVisible[movimentoIgnoto.id]" class="manual-suggestion-trigger">
          <Button
            icon="pi pi-plus"
            class="p-button-sm p-button-success p-button-rounded"
            aria-label="Aggiungi suggerimento manualmente"
            v-tooltip.top="'Aggiungi suggerimento manualmente'"
            @click="showManualSuggestion(movimentoIgnoto.id)"
          />
        </div>
        <div v-if="canSuggest && !isAssociated(movimentoIgnoto) && !movimentoIgnoto.suggerito && (!movimentoIgnoto.suggestions.length || manualSuggestionVisible[movimentoIgnoto.id])" class="manual-suggestion">
          <div class="manual-suggestion-heading">
            <label :for="`manual-invoice-${movimentoIgnoto.id}`">Numeri fattura da suggerire</label>
            <Button
              v-if="movimentoIgnoto.suggestions.length"
              icon="pi pi-times"
              class="p-button-rounded p-button-text p-button-danger"
              aria-label="Rimuovi suggerimento manuale"
              v-tooltip.top="'Rimuovi suggerimento manuale'"
              @click="hideManualSuggestion(movimentoIgnoto.id)"
            />
          </div>
          <Textarea
            :id="`manual-invoice-${movimentoIgnoto.id}`"
            v-model="manualInvoiceNumbers[movimentoIgnoto.id]"
            rows="3"
            autoResize
            class="w-full"
            placeholder="Inserisci uno o più numeri fattura, separati da virgola o a capo"
            :disabled="manualAssigning === movimentoIgnoto.id"
          />
          <small v-if="manualInvoiceAlreadySuggested(movimentoIgnoto)" class="manual-suggestion-error">
            Una o più fatture sono già presenti tra i suggerimenti del movimento.
          </small>
          <small>Inserisci i numeri esatti delle fatture, separati da virgola, punto e virgola o a capo.</small>
          <div class="manual-installment-option">
            <Checkbox
              :inputId="`manual-installment-${movimentoIgnoto.id}`"
              v-model="manualInstallmentFlags[movimentoIgnoto.id]"
              :binary="true"
              :disabled="manualAssigning === movimentoIgnoto.id"
            />
            <label :for="`manual-installment-${movimentoIgnoto.id}`">Le fatture indicate sono associate a una rata di dilazione</label>
          </div>
          <div class="manual-suggestion-actions">
            <Button
              label="Invia suggerimento"
              icon="pi pi-check"
              class="p-button-sm p-button-outlined"
              :loading="manualAssigning === movimentoIgnoto.id && managing !== movimentoIgnoto.id"
              :disabled="!hasManualInvoiceNumber(movimentoIgnoto) || manualInvoiceAlreadySuggested(movimentoIgnoto) || managing === movimentoIgnoto.id"
              @click="$emit('manual-assign', movimentoIgnoto, manualInvoiceNumbers[movimentoIgnoto.id], Boolean(manualInstallmentFlags[movimentoIgnoto.id]))"
            />
            <Button
              v-if="canAssociate"
              label="Associa movimento"
              icon="pi pi-link"
              class="p-button-sm p-button-success p-button-rounded"
              :loading="managing === movimentoIgnoto.id"
              :disabled="!hasManualInvoiceNumber(movimentoIgnoto) || manualInvoiceAlreadySuggested(movimentoIgnoto) || manualAssigning === movimentoIgnoto.id"
              @click="$emit('manual-manage', movimentoIgnoto, manualInvoiceNumbers[movimentoIgnoto.id], Boolean(manualInstallmentFlags[movimentoIgnoto.id]))"
            />
          </div>
        </div>
        <div class="suggestions-grid">
          <div v-for="suggestion in movimentoIgnoto.suggestions" :key="suggestion.id" class="suggestion-card" :class="{ 'suggestion-card-selected': isSelected(movimentoIgnoto, suggestion), 'suggestion-card-associated': isAssociated(movimentoIgnoto) && isSelected(movimentoIgnoto, suggestion) }">
            <div v-if="isSelected(movimentoIgnoto, suggestion)" class="selected-suggestion-badge mb-2">
              <i :class="isAssociated(movimentoIgnoto) ? 'pi pi-check-circle' : 'pi pi-star-fill'"></i>
              {{ isAssociated(movimentoIgnoto) ? 'Pagamento associato' : 'Suggerimento selezionato' }}
            </div>
            <div class="flex justify-content-between">
              <strong>{{ suggestion.natura === 'fattura' ? 'Fattura' : `Rata dilazione #${suggestion.id_rata}` }}</strong>
              <span>{{ suggestion.natura === 'fattura' ? suggestion.numero_fattura : `Fatture: ${suggestion.numero_fattura}` }}</span>
            </div>
            <div class="mt-2"><strong>{{ Number(suggestion.importo).toFixed(2) }} EUR</strong></div>
            <div class="mt-2">
              <a
                v-if="clientName(suggestion)"
                :href="inCallUrl(suggestion)"
                class="client-incall-link"
                target="_blank"
                rel="noopener noreferrer"
                title="Apri il cliente in In Call"
              >
                <i class="pi pi-external-link"></i>
                {{ clientName(suggestion) }}
              </a>
              <span v-else>Cliente non disponibile</span>
            </div>
            <div class="suggestion-details mt-2">
              <span v-if="suggestion.rappresentante">Rappresentante: {{ suggestion.rappresentante }}</span>
              <span v-if="suggestion.cliente_cellulare">Cell: {{ suggestion.cliente_cellulare }}</span>
              <span v-if="suggestion.cliente_email">Email: {{ suggestion.cliente_email }}</span>
            </div>
            <div class="suggestion-actions">
              <Button v-if="canSuggest && !isAssociated(movimentoIgnoto) && !movimentoIgnoto.suggerito" label="Segna come suggerimento" icon="pi pi-check" class="p-button-sm p-button-outlined" :loading="assigning === suggestion.id" @click="$emit('assign', movimentoIgnoto, suggestion)" />
              <Button v-if="(canSuggest || canManage) && movimentoIgnoto.suggerito && !isAssociated(movimentoIgnoto) && isSelected(movimentoIgnoto, suggestion)" label="Dissocia suggerimento" icon="pi pi-times" class="p-button-sm p-button-danger p-button-outlined" :loading="unassigning === movimentoIgnoto.id" @click="$emit('unassign', movimentoIgnoto)" />
              <Button v-if="canAssociate && !isAssociated(movimentoIgnoto) && (!movimentoIgnoto.suggerito || isSelected(movimentoIgnoto, suggestion))" label="Associa movimento" icon="pi pi-link" class="p-button-sm p-button-success p-button-rounded" :loading="managing === movimentoIgnoto.id" @click="$emit('manage', movimentoIgnoto, suggestion)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['movimentiIgnoti', 'loading', 'canSuggest', 'canManage', 'canAssociate', 'assigning', 'manualAssigning', 'unassigning', 'managing', 'deleting'],
  emits: ['assign', 'manual-assign', 'manual-manage', 'unassign', 'manage', 'request-delete'],
  data() {
    return {
      manualInvoiceNumbers: {},
      manualInstallmentFlags: {},
      manualSuggestionVisible: {}
    }
  },
  methods: {
    isAssociated(movimentoIgnoto) {
      return Number(movimentoIgnoto.gestito) === 1 || Number(movimentoIgnoto.estratto_conto_used) === 1
    },
    clientName(suggestion) {
      return suggestion.cliente_ragsoc || [suggestion.cliente_nome, suggestion.cliente_cognome].filter(Boolean).join(' ')
    },
    inCallUrl(suggestion) {
      return `/in-call?search_type=cliente&q=${encodeURIComponent(this.clientName(suggestion))}`
    },
    hasManualInvoiceNumber(movimentoIgnoto) {
      return this.invoiceNumberParts(this.manualInvoiceNumbers[movimentoIgnoto.id]).length > 0
    },
    manualInvoiceAlreadySuggested(movimentoIgnoto) {
      const enteredNumbers = this.invoiceNumberParts(this.manualInvoiceNumbers[movimentoIgnoto.id])
      if (!enteredNumbers.length) return false

      const suggestedNumbers = movimentoIgnoto.suggestions.flatMap((suggestion) => this.invoiceNumberParts(suggestion.numero_fattura))
      return enteredNumbers.some((number) => suggestedNumbers.includes(number))
    },
    invoiceNumberParts(value) {
      return String(value || '')
        .split(/[\r\n,;]+/)
        .map((number) => number.trim().toUpperCase())
        .filter(Boolean)
    },
    showManualSuggestion(movimentoId) {
      this.manualSuggestionVisible[movimentoId] = true
    },
    hideManualSuggestion(movimentoId) {
      delete this.manualSuggestionVisible[movimentoId]
      delete this.manualInvoiceNumbers[movimentoId]
      delete this.manualInstallmentFlags[movimentoId]
    },
    createdBy(movimentoIgnoto) {
      const name = [movimentoIgnoto.creatore_nome, movimentoIgnoto.creatore_cognome].filter(Boolean).join(' ')
      return name || (movimentoIgnoto.user_id_creazione ? `Utente #${movimentoIgnoto.user_id_creazione}` : '-')
    },
    suggestedBy(movimentoIgnoto) {
      const name = [movimentoIgnoto.suggeritore_nome, movimentoIgnoto.suggeritore_cognome].filter(Boolean).join(' ')
      return name || (movimentoIgnoto.user_id_suggerimento ? `Utente #${movimentoIgnoto.user_id_suggerimento}` : '-')
    },
    managedBy(movimentoIgnoto) {
      const name = [
        movimentoIgnoto.gestore_nome || movimentoIgnoto.estratto_conto_gestore_nome,
        movimentoIgnoto.gestore_cognome || movimentoIgnoto.estratto_conto_gestore_cognome
      ].filter(Boolean).join(' ')
      const userId = movimentoIgnoto.user_id_gestione || movimentoIgnoto.estratto_conto_user_id
      return name || (userId ? `Utente #${userId}` : '-')
    },
    isSelected(movimentoIgnoto, suggestion) {
      return Number(movimentoIgnoto.id_suggerimento) === Number(suggestion.id)
    },
    formatDate(date) {
      return date ? String(date).substring(0, 10).split('-').reverse().join('/') : '-'
    },
    formatDateTime(date) {
      if (!date) return '-'

      const [datePart, timePart = ''] = String(date).replace('T', ' ').split(' ')
      const formattedDate = datePart.split('-').reverse().join('/')
      return `${formattedDate}${timePart ? ` ${timePart.substring(0, 5)}` : ''}`
    }
  }
}
</script>

<style scoped>
.movimento-ignoto-row { border: 1px solid #dbeafe; border-radius: 8px; padding: 1rem; }
.movement-layout { display: flex; gap: 1rem; }
.movement-panel, .suggestions-panel { flex: 1; min-width: 0; }
.movement-panel { padding: 1rem; border-radius: 6px; background: #f8fbff; }
.movement-heading { display: flex; align-items: center; justify-content: space-between; gap: .75rem; }
.movement-title-group { display: flex; align-items: center; gap: .25rem; }
.movement-delete-button { width: 1.75rem !important; height: 1.75rem !important; padding: 0 !important; }
.panel-title { font-weight: 600; color: #1e3a5f; }
.status-badge { display: inline-flex; align-items: center; gap: .35rem; padding: .3rem .55rem; border-radius: 999px; font-size: .75rem; font-weight: 600; white-space: nowrap; }
.status-suggested { color: #166534; background: #dcfce7; }
.status-associated { color: #075985; background: #e0f2fe; }
.status-pending { color: #92400e; background: #fef3c7; }
.is-suggested { border-color: #bbf7d0; }
.is-suggested .movement-panel { background: #f5fff8; }
.is-pending { border-color: #fde68a; }
.movement-amount { margin-top: .35rem; color: #1683ee; font-size: 1.25rem; font-weight: 700; }
.movement-description { margin-top: .75rem; color: #475569; line-height: 1.4; }
.movement-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .75rem; margin-top: 1rem; }
.movement-details span { display: block; color: #64748b; font-size: .75rem; }
.movement-details strong { display: block; margin-top: .15rem; color: #1e293b; }
.movement-history-list { display: flex; grid-column: 1 / -1; flex-direction: column; gap: .3rem; }
.movement-details .movement-history { display: flex; align-items: baseline; gap: .3rem; }
.movement-details .movement-history span,
.movement-details .movement-history strong { display: inline; margin-top: 0; }
.suggestions-panel > .panel-title { margin-bottom: .75rem; }
.manual-suggestion-trigger { margin-bottom: .75rem; }
.manual-suggestion { display: flex; flex-direction: column; align-items: flex-start; gap: .65rem; margin-bottom: .75rem; padding: 1rem; border: 1px dashed #93c5fd; border-radius: 6px; background: #f8fbff; }
.manual-suggestion-heading { display: flex; align-items: center; justify-content: space-between; width: 100%; gap: .5rem; }
.manual-suggestion label { color: #1e3a5f; font-weight: 600; }
.manual-suggestion small { color: #64748b; }
.manual-suggestion-error { color: #b91c1c !important; font-weight: 600; }
.manual-installment-option { display: flex; align-items: center; gap: .55rem; }
.manual-installment-option label { cursor: pointer; font-weight: 400; }
.manual-suggestion-actions { display: flex; align-self: center; flex-wrap: wrap; justify-content: center; gap: .5rem; }
.suggestions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: .75rem; }
.suggestion-card { display: flex; flex-direction: column; height: 100%; padding: .9rem; border: 1px solid #e2e8f0; border-radius: 6px; background: #f8fbff; }
.suggestion-card-selected { border: 2px solid #3b82f6; background: #eff6ff; box-shadow: 0 6px 18px rgba(59, 130, 246, .12); }
.suggestion-card-associated { border-color: #22c55e; background: #f0fdf4; box-shadow: 0 6px 18px rgba(34, 197, 94, .12); }
.selected-suggestion-badge { display: inline-flex; align-items: center; align-self: flex-start; gap: .35rem; padding: .28rem .55rem; border-radius: 999px; color: #1d4ed8; background: #dbeafe; font-size: .75rem; font-weight: 700; }
.suggestion-card-associated .selected-suggestion-badge { color: #166534; background: #dcfce7; }
.suggestion-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem; margin-top: auto; padding-top: .75rem; }
.suggestion-actions :deep(.p-button) { display: inline-flex; align-items: center; justify-content: center; }
.suggestion-actions :deep(.p-button-icon),
.suggestion-actions :deep(.p-button-label) { line-height: 1; }
.suggestion-details { display: flex; flex-direction: column; gap: .25rem; color: #475569; font-size: .9rem; }
.client-incall-link { display: inline-flex; align-items: center; gap: .35rem; color: #1683ee; font-weight: 600; text-decoration: none; }
.client-incall-link:hover { text-decoration: underline; }
@media (max-width: 900px) {
  .movement-layout { flex-direction: column; }
  .movement-heading { align-items: flex-start; flex-direction: column; }
}
</style>
